import { z } from 'zod';
export const RecurringInvoicePeriodicitySchema = z.enum([
    'MONTHLY',
    'BIMONTHLY',
    'QUARTERLY',
    'SEMIANNUAL',
    'YEARLY',
]);
export const RecurringInvoicePeriodicityLabel = {
    MONTHLY: 'Mensual',
    BIMONTHLY: 'Bimestral',
    QUARTERLY: 'Trimestral',
    SEMIANNUAL: 'Semestral',
    YEARLY: 'Anual',
};
export const RecurringInvoiceStatusSchema = z.enum(['ACTIVE', 'PAUSED', 'CANCELED', 'COMPLETED']);
export const RecurringInvoiceStatusLabel = {
    ACTIVE: 'Activa',
    PAUSED: 'Pausada',
    CANCELED: 'Cancelada',
    COMPLETED: 'Completada',
};
/**
 * Ancla de inicio del mantenimiento recurrente: 'DATE' usa una fecha concreta;
 * las fases de ejecución hacen que la iguala empiece a facturarse al llegar el
 * proyecto a esa fase (creada PAUSED y activada al alcanzarla). Vive aquí (y no
 * en opportunities.ts) para que el DTO de iguala pueda usarlo sin ciclo.
 */
export const RecurringStartAnchorSchema = z.enum([
    'DATE',
    'ONBOARDING',
    'EJECUCION',
    'FINALIZADO',
    'SOPORTE',
]);
const decimalString = z
    .union([z.number(), z.string()])
    .transform((v) => (typeof v === 'number' ? v.toString() : v));
export const RecurringInvoiceLineInputSchema = z.object({
    position: z.number().int().min(0).default(0),
    /** F35 — opcional: producto del catálogo del que se prellenan description/unitPriceCents/vatRate si no se especifican. */
    productId: z.string().nullable().optional(),
    /** Requerido salvo que llegue productId. */
    description: z.string().min(1).max(500).optional(),
    quantity: decimalString.default('1'),
    /** Requerido salvo que llegue productId. */
    unitPriceCents: z.number().int().optional(),
    /** Coste interno por unidad (mismo criterio que unitPriceCents). Campo interno (margen). */
    costCents: z.number().int().min(0).default(0),
    discountPct: decimalString.default('0'),
    /** Requerido salvo que llegue productId. */
    vatRate: decimalString.optional(),
    isSupplied: z.boolean().default(false),
});
export const RecurringInvoiceLineDtoSchema = z.object({
    id: z.string(),
    productId: z.string().nullable(),
    position: z.number().int(),
    description: z.string(),
    quantity: decimalString,
    unitPriceCents: z.number().int(),
    costCents: z.number().int(),
    discountPct: decimalString,
    vatRate: decimalString,
    isSupplied: z.boolean(),
});
export const RecurringInvoiceDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    clientDisplayName: z.string(),
    opportunityId: z.string().nullable(),
    opportunityCode: z.string().nullable(),
    seriesId: z.string().nullable(),
    seriesPrefix: z.string().nullable(),
    name: z.string(),
    notes: z.string().nullable(),
    periodicity: RecurringInvoicePeriodicitySchema,
    dayOfMonth: z.number().int(),
    irpfRate: z.string(),
    status: RecurringInvoiceStatusSchema,
    nextRunDate: z.string().datetime(),
    lastRunAt: z.string().datetime().nullable(),
    generatedCount: z.number().int(),
    /** F42/M6 — límite del contrato: nº máximo de cuotas y/o fecha fin. null+null = sin fecha límite (alerta de renovación). */
    maxOccurrences: z.number().int().nullable(),
    endsAt: z.string().datetime().nullable(),
    /** Ancla de arranque por fase (mantenimiento diferido): iguala PAUSED hasta llegar a ella. null = sin ancla. */
    startAnchor: RecurringStartAnchorSchema.nullable(),
    createdAt: z.string().datetime(),
    createdByName: z.string().nullable(),
    lines: z.array(RecurringInvoiceLineDtoSchema),
});
export const CreateRecurringInvoiceSchema = z.object({
    clientId: z.string(),
    opportunityId: z.string().nullable().optional(),
    seriesId: z.string().nullable().optional(),
    name: z.string().min(2).max(200),
    notes: z.string().max(2000).nullable().optional(),
    periodicity: RecurringInvoicePeriodicitySchema.default('MONTHLY'),
    dayOfMonth: z.coerce.number().int().min(1).max(28).default(1),
    irpfRate: decimalString.optional(),
    /** Fecha de la primera factura a generar (yyyy-mm-dd o ISO). Si null, calculamos a partir de periodicity+dayOfMonth desde hoy. */
    startDate: z.string().nullable().optional(),
    /** M6 — límite del contrato (opcional): nº de cuotas y/o fecha fin. */
    maxOccurrences: z.number().int().positive().nullable().optional(),
    endsAt: z.string().nullable().optional(),
    lines: z.array(RecurringInvoiceLineInputSchema).min(1),
});
export const UpdateRecurringInvoiceSchema = CreateRecurringInvoiceSchema.partial().extend({
    status: RecurringInvoiceStatusSchema.optional(),
    nextRunDate: z.string().nullable().optional(),
});
export const RecurringInvoiceListResponseSchema = z.object({
    items: z.array(RecurringInvoiceDtoSchema),
});
//# sourceMappingURL=recurring.js.map